/**
 * Sprint 26.1 — pure helpers for the federated Routine run flow.
 *
 * Kuvio executes nothing. `run_routine` returns a {@link CompiledRoutineProcedure}
 * for the host to run; the host (or the console's degraded path) then records the
 * outcome via `record_routine_run`, which logs the run as an Activity server-side
 * (Sprint 26.1.1) — the console no longer writes that Activity itself.
 *
 * Two pure seams worth testing:
 *  - {@link formatRoutineProcedure}: the copyable, host-ready text block.
 *  - {@link toRoutineRunLines}: routine runs pulled from an agent's Activity feed
 *    for the Overview "Recently" timeline.
 */

import type { Activity, CompiledRoutineProcedure } from "../../lib/types";

/**
 * The host-ready text a user copies when no executor is wired (degraded path).
 * Header carries the name + purpose + cadence so the block is self-describing;
 * the body is the resolved procedure (or a clear placeholder when empty).
 */
export function formatRoutineProcedure(p: CompiledRoutineProcedure): string {
  const lines = [
    `# Routine: ${p.name}`,
    `# Purpose: ${p.purpose}`,
    `# Cadence: ${p.cadence}`,
  ];
  if (p.completion_criteria) lines.push(`# Done when: ${p.completion_criteria}`);
  if (p.expected_outputs.length > 0) lines.push(`# Expected outputs: ${p.expected_outputs.join(", ")}`);
  lines.push("", p.procedure?.trim() || "(This routine has no procedure body.)");
  return lines.join("\n");
}

/** A routine run shaped for the Overview "Recently" timeline. */
export interface RoutineRunLine {
  id: string;
  occurred_at: string;
  /** The Activity subject, e.g. "Ran routine: Daily ledger sweep". */
  label: string;
  summary: string | null;
}

/**
 * Pull routine-run rows out of an agent's Activity feed for the "Recently"
 * surface. A run is any Activity carrying `routine_ref` (set by record_routine_run).
 */
export function toRoutineRunLines(activities: Activity[]): RoutineRunLine[] {
  return activities
    .filter((a) => !!a.routine_ref && !a.archived_at)
    .map((a) => ({ id: a.id, occurred_at: a.occurred_at, label: a.subject, summary: a.summary }));
}
